// File: apps/web/src/features/import/PreviewCard.tsx
// Version: 2026.4.7 | Sprint 7 Wave 3 — Preview-before-import card
//
// Renders the ImportPreviewResponse as a glassy summary card with:
//   • Top metrics row (count, cost, duration)
//   • Date range
//   • Checklist of observations
//   • Confirm + Cancel buttons
//
// The parent decides what "Confirm" does — typically calls
// useImportClaudeExport().importExport(file).

import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { GlassPanel, MetricCard, SectionLabel } from '../../components/engine/EnginePageShell';
import type { ImportPreviewResponse, ChecklistSeverity } from '../../lib/api/types-import';

interface PreviewCardProps {
  preview:    ImportPreviewResponse;
  onConfirm:  () => void;
  onCancel:   () => void;
  busy?:      boolean;
}

export function PreviewCard({ preview, onConfirm, onCancel, busy = false }: PreviewCardProps) {
  const dateRange = formatDateRange(preview.earliestConversation, preview.latestConversation);
  const hours     = preview.totalEstimatedDurationMinutes / 60;
  const tokens    = preview.totalEstimatedInputTokens + preview.totalEstimatedOutputTokens;

  return (
    <GlassPanel accent={C.cyan}>
      <div style={{ padding: 24 }}>
        <SectionLabel color={C.cyan}>PREVIEW · NOTHING WRITTEN YET</SectionLabel>

        <div style={{
          fontSize:    24,
          fontWeight:  600,
          color:       C.text,
          marginTop:   8,
          marginBottom: 4,
          fontFamily:  F.display,
        }}>
          {preview.newConversationCount === 0
            ? 'No new conversations to import'
            : `${preview.newConversationCount} new conversation${preview.newConversationCount === 1 ? '' : 's'} ready to stage`}
        </div>

        <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
          {dateRange}
        </div>

        {/* Metrics row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}>
          <MetricCard
            label="Found"
            value={preview.conversationCount.toLocaleString()}
            subtitle="in export"
            color={C.cyan}
          />
          <MetricCard
            label="New"
            value={preview.newConversationCount.toLocaleString()}
            subtitle={preview.duplicateCount > 0 ? `${preview.duplicateCount} dup` : 'none staged'}
            color={C.neon}
          />
          <MetricCard
            label="Est. cost"
            value={`$${preview.totalEstimatedCostUSD.toFixed(2)}`}
            subtitle="Sonnet 4 pricing"
            color={C.violet}
          />
          <MetricCard
            label="Active time"
            value={hours >= 1 ? `${hours.toFixed(1)}h` : `${preview.totalEstimatedDurationMinutes}m`}
            subtitle="estimated"
            color={C.amber}
          />
        </div>

        {/* Token detail */}
        <div style={{
          padding: '12px 16px',
          background: 'rgba(8,16,32,0.6)',
          border: `1px solid ${C.faint}`,
          borderRadius: 8,
          marginBottom: 24,
          fontFamily: F.mono,
          fontSize: 12,
          color: C.dim,
        }}>
          <span style={{ color: C.muted }}>{tokens.toLocaleString()}</span> total tokens estimated
          {' · '}
          <span style={{ color: C.muted }}>{preview.totalEstimatedInputTokens.toLocaleString()}</span> in
          {' · '}
          <span style={{ color: C.muted }}>{preview.totalEstimatedOutputTokens.toLocaleString()}</span> out
          {preview.heuristicProjectMatchCount > 0 && (
            <>
              {' · '}
              <span style={{ color: C.neon }}>{preview.heuristicProjectMatchCount}</span> heuristic project match{preview.heuristicProjectMatchCount === 1 ? '' : 'es'}
            </>
          )}
        </div>

        {/* Checklist */}
        <div style={{ marginBottom: 24 }}>
          <SectionLabel>WHAT WILL HAPPEN</SectionLabel>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {preview.checklist.map((item, i) => (
              <ChecklistRow key={i} severity={item.severity} message={item.message} />
            ))}
          </div>
        </div>

        {/* Warnings (if any) */}
        {preview.warnings && preview.warnings.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <SectionLabel color={C.amber}>WARNINGS · {preview.warnings.length}</SectionLabel>
            <div style={{
              marginTop: 8,
              maxHeight: 160,
              overflowY: 'auto',
              padding: 12,
              background: `${C.amber}08`,
              border: `1px solid ${C.amber}28`,
              borderRadius: 8,
              fontFamily: F.mono,
              fontSize: 12,
              color: C.muted,
            }}>
              {preview.warnings.map((w, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <span style={{ color: C.amber }}>{w.sourceUuid.slice(0, 8)}</span>{' '}
                  {w.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={cancelButtonStyle(busy)}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || preview.newConversationCount === 0}
            style={confirmButtonStyle(busy || preview.newConversationCount === 0)}
          >
            {busy
              ? 'Importing…'
              : preview.willRunInBackground
                ? `Stage ${preview.newConversationCount} & classify in background →`
                : `Stage ${preview.newConversationCount} & classify now →`}
          </button>
        </div>
      </div>
    </GlassPanel>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function ChecklistRow({ severity, message }: { severity: ChecklistSeverity; message: string }) {
  const palette = SEVERITY_PALETTE[severity];
  return (
    <div style={{
      display:      'flex',
      alignItems:   'flex-start',
      gap:          12,
      padding:      '8px 12px',
      background:   palette.bg,
      border:       `1px solid ${palette.border}`,
      borderRadius: 6,
      fontSize:     13,
      color:        C.text,
      fontFamily:   F.display,
    }}>
      <span style={{
        color:    palette.fg,
        fontSize: 14,
        flexShrink: 0,
        lineHeight: 1.4,
      }}>
        {palette.icon}
      </span>
      <span style={{ lineHeight: 1.5 }}>{message}</span>
    </div>
  );
}

const SEVERITY_PALETTE: Record<ChecklistSeverity, { icon: string; fg: string; bg: string; border: string }> = {
  ok:    { icon: '✓', fg: C.neon,   bg: `${C.neon}08`,   border: `${C.neon}28`   },
  info:  { icon: '•', fg: C.cyan,   bg: `${C.cyan}08`,   border: `${C.cyan}28`   },
  warn:  { icon: '!', fg: C.amber,  bg: `${C.amber}08`,  border: `${C.amber}28`  },
  error: { icon: '✕', fg: C.coral,  bg: `${C.coral}08`,  border: `${C.coral}28`  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatDateRange(earliest: string | null, latest: string | null): string {
  if (!earliest || !latest) return 'No date range';
  const e = new Date(earliest);
  const l = new Date(latest);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  if (e.toDateString() === l.toDateString()) return fmt(e);
  return `${fmt(e)} → ${fmt(l)}`;
}

const cancelButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding:      '12px 24px',
  fontSize:     14,
  fontWeight:   500,
  fontFamily:   F.display,
  background:   'transparent',
  color:        C.muted,
  border:       `1px solid ${C.faint}`,
  borderRadius: 8,
  cursor:       disabled ? 'not-allowed' : 'pointer',
  opacity:      disabled ? 0.5 : 1,
  transition:   'all 0.15s ease',
});

const confirmButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding:      '12px 24px',
  fontSize:     14,
  fontWeight:   600,
  fontFamily:   F.display,
  background:   disabled ? C.faint : C.neon,
  color:        disabled ? C.dim   : '#050910',
  border:       'none',
  borderRadius: 8,
  cursor:       disabled ? 'not-allowed' : 'pointer',
  boxShadow:    disabled ? 'none' : `0 0 24px ${C.neonGlow}`,
  transition:   'all 0.15s ease',
});
